import { type ReactNode } from 'react';

import { accessValue } from '../../../core/utils/accessors/access-value.js';
import type { TrendDirection } from '../../core/components/single-value-renderer/components/trend/types/trend-direction.js';
import type { SparklineXAxisProps } from '../../core/components/sparkline-display/types/sparkline-x-axis-props.js';
import type { SparklineYAxisProps } from '../../core/components/sparkline-display/types/sparkline-y-axis-props.js';
import type { Timeseries } from '../../core/types/timeseries.js';
import type { SingleValueGridAccessorsConfig } from '../types/single-value-grid.js';

type TrendData = {
  direction?: TrendDirection;
  value?: number;
  label?: string;
};

type SingleValueData = {
  label?: string;
  prefixIcon?: ReactNode;
  sparkline?: {
    xAxis?: SparklineXAxisProps;
    yAxis?: SparklineYAxisProps;
    data?: Timeseries | Timeseries[];
  };
  value?: number | string;
  trendData?: TrendData;
};

function processTrendData(
  data: Record<string, unknown>,
  trendAccessors: NonNullable<SingleValueGridAccessorsConfig['trend']>,
): TrendData {
  const trendData: TrendData = {};

  if (trendAccessors.directionAccessor) {
    trendData.direction = accessValue<TrendDirection>(
      data,
      trendAccessors.directionAccessor,
    );
  }

  if (trendAccessors.valueAccessor) {
    trendData.value = accessValue<number>(data, trendAccessors.valueAccessor);
  }

  if (trendAccessors.labelAccessor) {
    trendData.label = accessValue<string>(data, trendAccessors.labelAccessor);
  }

  return trendData;
}

function processSparklineData(
  data: Record<string, unknown>,
  sparklineAccessors: NonNullable<SingleValueGridAccessorsConfig['sparkline']>,
): SingleValueData['sparkline'] {
  const sparklineData: SingleValueData['sparkline'] = {};

  if (sparklineAccessors.xAxis) {
    sparklineData.xAxis = sparklineAccessors.xAxis;
  }

  if (sparklineAccessors.yAxis) {
    sparklineData.yAxis = sparklineAccessors.yAxis;
  }

  if (sparklineAccessors.dataAccessor) {
    sparklineData.data = accessValue<Timeseries | Timeseries[]>(
      data,
      sparklineAccessors.dataAccessor,
    );
  }

  return sparklineData;
}

/**
 * Retrieves data from the provided object using accessors specified in the accessors' configuration.
 *
 * @param data - The object containing the data.
 * @param accessors - The configuration specifying the accessors.
 * @returns The accessed data object.
 */
export function getDataFromAccessors(
  data: Record<string, unknown>,
  accessors: SingleValueGridAccessorsConfig,
) {
  const accessedData: SingleValueData = {};

  if (accessors.labelAccessor) {
    accessedData.label = accessValue<string>(data, accessors.labelAccessor);
  }

  if (accessors.prefixIconAccessor) {
    accessedData.prefixIcon = accessValue<ReactNode>(
      data,
      accessors.prefixIconAccessor,
    );
  }

  if (accessors.value?.dataAccessor) {
    accessedData.value = accessValue<number | string>(
      data,
      accessors.value?.dataAccessor,
    );
  }

  if (accessors.trend) {
    accessedData.trendData = processTrendData(data, accessors.trend);
  }

  if (accessors.sparkline) {
    accessedData.sparkline = processSparklineData(data, accessors.sparkline);
  }

  return accessedData;
}
